import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, onSnapshot, doc, updateDoc, where, writeBatch, getDocs } from 'firebase/firestore';
import { db, functions } from '../../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { User } from '../../types';
import { toast } from '@/lib/toast';
import { Search, Ban, Trash2, Edit2, CheckCircle, Mail, Phone, Calendar, Shield, UserX, RefreshCw, Star } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ClientStampModal from './ClientStampModal';

interface ClientWithStats extends Omit<User, 'userId'> {
 uid: string; // User ID from Firebase Auth
 totalTickets?: number;
 bannedUntil?: string | null; // ISO date string
 isBanned?: boolean;
 isDeleted?: boolean; // Soft deleted account
 deletedAt?: any; // Timestamp when account was deleted
}

interface EditingClient {
 userId: string;
 displayName?: string;
 email?: string;
 phoneNumber?: string;
}

interface Props {
 selectedFranchiseId: string;
 selectedBranchId: string; // Not used - clients belong to franchise, not branch
}

const ClientsManagement: React.FC<Props> = ({ selectedFranchiseId }) => {
 const { t } = useTranslation();
 const { user: currentUser } = useAuth();
 const isSuperAdmin = currentUser?.role === 'super_admin';
 const [clients, setClients] = useState<ClientWithStats[]>([]);
 const [loading, setLoading] = useState(true);
 const [editingClient, setEditingClient] = useState<EditingClient | null>(null);
 const [searchTerm, setSearchTerm] = useState('');
 const [showBannedOnly, setShowBannedOnly] = useState(false);
 const [showDeletedOnly, setShowDeletedOnly] = useState(false);
 const [processingAction, setProcessingAction] = useState<string | null>(null);
 const [stampClient, setStampClient] = useState<ClientWithStats | null>(null);

 useEffect(() => {
 let unsubscribeUsers: (() => void) | undefined;

 // Build query based on selected franchise only
 // Clients belong to a franchise and can visit any branch
 const queryConstraints = [where('role', '==', 'client')];

 // Filter by franchise if a specific franchise is selected (not 'all')
 if (selectedFranchiseId && selectedFranchiseId !== 'all') {
 queryConstraints.push(where('franchiseId', '==', selectedFranchiseId));
 }

 const usersQuery = query(
 collection(db, 'users'),
 ...queryConstraints
 );

 unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
 const clientsList: ClientWithStats[] = [];

 snapshot.docs.forEach((docSnap) => {
 const userData = docSnap.data() as User;
 const bannedUntil = (userData as any).bannedUntil;
 const isBanned = bannedUntil ? new Date(bannedUntil) > new Date() : false;
 const isDeleted = (userData as any).isDeleted === true;
 const deletedAt = (userData as any).deletedAt;

 clientsList.push({
 uid: docSnap.id,
 ...userData,
 totalTickets: 0, // Could be calculated later if needed
 bannedUntil: bannedUntil || null,
 isBanned,
 isDeleted,
 deletedAt,
 });
 });

 setClients(clientsList);
 setLoading(false);
 }, (error) => {
 console.error('Error loading clients:', error);
 toast.error(t('admin.clients.errorLoadingClients'));
 setLoading(false);
 });

 return () => {
 if (unsubscribeUsers) unsubscribeUsers();
 };
 }, [selectedFranchiseId]);

 // Filter clients by search term, banned status, and deleted status
 const filteredClients = clients.filter((client) => {
 const matchesSearch =
 client.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
 client.phoneNumber?.includes(searchTerm);

 const matchesBannedFilter = showBannedOnly ? client.isBanned : true;

 // For deleted filter:
 // - If showDeletedOnly is true, show only deleted accounts
 // - If showDeletedOnly is false, hide deleted accounts (unless super admin can see all)
 const matchesDeletedFilter = showDeletedOnly
 ? client.isDeleted === true
 : (isSuperAdmin ? true : client.isDeleted !== true);

 return matchesSearch && matchesBannedFilter && matchesDeletedFilter;
 });

 const handleBanClient = async (userId: string, currentlyBanned: boolean) => {
 try {
 setProcessingAction(userId);

 if (currentlyBanned) {
 // Unban: remove bannedUntil
 await updateDoc(doc(db, 'users', userId), {
 bannedUntil: null,
 updatedAt: new Date(),
 });
 toast.success(t('admin.clients.clientUnbanned'));
 } else {
 // Ban for 30 days
 const bannedUntil = new Date();
 bannedUntil.setDate(bannedUntil.getDate() + 30);

 await updateDoc(doc(db, 'users', userId), {
 bannedUntil: bannedUntil.toISOString(),
 updatedAt: new Date(),
 });
 toast.success(t('admin.clients.clientBanned'));
 }
 } catch (error) {
 console.error('Error banning/unbanning client:', error);
 toast.error(t('admin.clients.errorBanningClient'));
 } finally {
 setProcessingAction(null);
 }
 };

 const handleDeleteClient = async (userId: string, userName: string) => {
 const confirmed = window.confirm(
 `${t('admin.clients.confirmDeleteClient', { name: userName })}\n\n` +
 `${t('admin.clients.deleteWarningList')}\n\n` +
 t('admin.clients.deleteWarningUndo')
 );

 if (!confirmed) return;

 try {
 setProcessingAction(userId);

 const batch = writeBatch(db);

 // 1. Delete all user's queue tickets
 const ticketsQuery = query(collection(db, 'queues'), where('userId', '==', userId));
 const ticketsSnap = await getDocs(ticketsQuery);
 ticketsSnap.forEach((doc) => {
 batch.delete(doc.ref);
 });

 // 2. Delete all user's notifications
 const notificationsQuery = query(collection(db, 'notifications'), where('userId', '==', userId));
 const notificationsSnap = await getDocs(notificationsQuery);
 notificationsSnap.forEach((doc) => {
 batch.delete(doc.ref);
 });

 // 3. Delete user document
 batch.delete(doc(db, 'users', userId));

 await batch.commit();

 // 4. Call cloud function to delete Firebase Auth user
 try {
 const deleteUserAuth = httpsCallable(functions, 'deleteUserAccount');
 await deleteUserAuth({ userId });
 } catch (authError) {
 console.warn('Error deleting auth user (may not exist):', authError);
 }

 toast.success(t('admin.clients.clientDeleted'));
 } catch (error) {
 console.error('Error deleting client:', error);
 toast.error(t('admin.clients.errorDeletingClient'));
 } finally {
 setProcessingAction(null);
 }
 };

 const handleEditClient = (client: ClientWithStats) => {
 setEditingClient({
 userId: client.uid!,
 displayName: client.displayName || '',
 email: client.email || '',
 phoneNumber: client.phoneNumber || '',
 });
 };

 const handleSaveEdit = async () => {
 if (!editingClient) return;

 try {
 setProcessingAction(editingClient.userId);

 await updateDoc(doc(db, 'users', editingClient.userId), {
 displayName: editingClient.displayName,
 phoneNumber: editingClient.phoneNumber,
 updatedAt: new Date(),
 });

 toast.success(t('admin.clients.clientUpdated'));
 setEditingClient(null);
 } catch (error) {
 console.error('Error updating client:', error);
 toast.error(t('admin.clients.errorUpdatingClient'));
 } finally {
 setProcessingAction(null);
 }
 };

 // Handle restore deleted account (super admin only)
 const handleRestoreAccount = async (userId: string) => {
 try {
 setProcessingAction(userId);

 const restoreAccount = httpsCallable(functions, 'restoreClientAccount');
 await restoreAccount({ userId });

 toast.success(t('admin.clients.accountRestored', 'Cuenta restaurada correctamente'));
 } catch (error) {
 console.error('Error restoring account:', error);
 toast.error(t('admin.clients.restoreError', 'Error al restaurar la cuenta'));
 } finally {
 setProcessingAction(null);
 }
 };

 const getStatusBadge = (client: ClientWithStats) => {
 // Show deleted status first (takes priority)
 if (client.isDeleted) {
 return (
 <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-surface-2 text-ink border border-hairline bg-surface-2 text-ink-muted border-hairline">
 <UserX size={12} />
 {t('admin.clients.deleted', 'Eliminado')}
 </span>
 );
 }

 if (client.isBanned) {
 return (
 <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-danger/10 text-danger border border-danger/30 bg-danger/10 text-danger border-danger/30">
 <Ban size={12} />
 {t('common.banned')}
 </span>
 );
 }

 return (
 <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-success/10 text-success border border-success/30 bg-success/10 text-success border-success/30">
 <CheckCircle size={12} />
 {t('common.active')}
 </span>
 );
 };

 if (loading) {
 return (
 <div className="flex items-center justify-center py-12">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-info/30"></div>
 </div>
 );
 }

 return (
 <div className="space-y-6">
 {/* Header with Search and Filters */}
 <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
 <div className="flex-1 max-w-md">
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-subtle" size={20} />
 <input
 type="text"
 placeholder={t('admin.clients.searchPlaceholder')}
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="w-full pl-10 pr-4 py-2 border border-hairline rounded-lg bg-surface text-ink focus:ring-2 focus:ring-focus focus:border-transparent"
 />
 </div>
 </div>

 <div className="flex gap-3">
 <button
 onClick={() => {
 setShowBannedOnly(!showBannedOnly);
 if (!showBannedOnly) setShowDeletedOnly(false);
 }}
 className={`px-4 py-2 rounded-lg font-medium transition ${
 showBannedOnly
 ? 'bg-danger text-white'
 : 'bg-surface-2 text-ink-muted'
 }`}
 >
 {showBannedOnly ? t('common.showAll') : t('admin.clients.bannedOnly')}
 </button>

 {/* Show deleted accounts filter - only for super admin */}
 {isSuperAdmin && (
 <button
 onClick={() => {
 setShowDeletedOnly(!showDeletedOnly);
 if (!showDeletedOnly) setShowBannedOnly(false);
 }}
 className={`px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
 showDeletedOnly
 ? 'bg-surface-3 text-ink'
 : 'bg-surface-2 text-ink-muted'
 }`}
 >
 <UserX size={16} />
 {showDeletedOnly ? t('common.showAll') : t('admin.clients.showDeleted', 'Ver Eliminados')}
 </button>
 )}
 </div>
 </div>

 {/* Stats */}
 <div className={`grid grid-cols-1 gap-4 ${isSuperAdmin ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
 <div className="bg-surface p-4 rounded-lg border border-hairline">
 <div className="flex items-center gap-3">
 <div className="p-3 bg-info/10 rounded-lg">
 <Shield size={24} className="text-info" />
 </div>
 <div>
 <p className="text-sm text-ink-muted">{t('admin.clients.totalClients')}</p>
 <p className="text-2xl font-bold text-ink">
 {clients.filter(c => !c.isDeleted).length}
 </p>
 </div>
 </div>
 </div>

 <div className="bg-surface p-4 rounded-lg border border-hairline">
 <div className="flex items-center gap-3">
 <div className="p-3 bg-success/10 rounded-lg">
 <CheckCircle size={24} className="text-success" />
 </div>
 <div>
 <p className="text-sm text-ink-muted">{t('admin.clients.activeClients')}</p>
 <p className="text-2xl font-bold text-ink">
 {clients.filter(c => !c.isBanned && !c.isDeleted).length}
 </p>
 </div>
 </div>
 </div>

 <div className="bg-surface p-4 rounded-lg border border-hairline">
 <div className="flex items-center gap-3">
 <div className="p-3 bg-danger/10 rounded-lg">
 <Ban size={24} className="text-danger" />
 </div>
 <div>
 <p className="text-sm text-ink-muted">{t('admin.clients.bannedClients')}</p>
 <p className="text-2xl font-bold text-ink">
 {clients.filter(c => c.isBanned && !c.isDeleted).length}
 </p>
 </div>
 </div>
 </div>

 {/* Deleted accounts stat - only for super admin */}
 {isSuperAdmin && (
 <div className="bg-surface p-4 rounded-lg border border-hairline">
 <div className="flex items-center gap-3">
 <div className="p-3 bg-surface-2 rounded-lg">
 <UserX size={24} className="text-ink-muted" />
 </div>
 <div>
 <p className="text-sm text-ink-muted">{t('admin.clients.deletedAccounts', 'Cuentas Eliminadas')}</p>
 <p className="text-2xl font-bold text-ink">
 {clients.filter(c => c.isDeleted).length}
 </p>
 </div>
 </div>
 </div>
 )}
 </div>

 {/* Clients Table */}
 <div className="bg-surface rounded-lg border border-hairline overflow-hidden">
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead className="bg-surface-2 border-b border-hairline">
 <tr>
 <th className="text-left py-3 px-4 font-semibold text-sm text-ink-muted">{t('admin.clients.client')}</th>
 <th className="text-left py-3 px-4 font-semibold text-sm text-ink-muted">{t('admin.clients.contact')}</th>
 <th className="text-left py-3 px-4 font-semibold text-sm text-ink-muted">{t('admin.clients.registration')}</th>
 <th className="text-left py-3 px-4 font-semibold text-sm text-ink-muted">{t('common.status')}</th>
 <th className="text-right py-3 px-4 font-semibold text-sm text-ink-muted">{t('common.actions')}</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-hairline">
 {filteredClients.length === 0 ? (
 <tr>
 <td colSpan={5} className="py-8 text-center text-ink-subtle">
 {searchTerm ? t('admin.clients.noClientsFound') : t('admin.clients.noClients')}
 </td>
 </tr>
 ) : (
 filteredClients.map((client) => (
 <tr key={client.uid} className="hover:bg-surface-2 transition">
 <td className="py-3 px-4">
 <div className="flex flex-col">
 <span className="font-semibold text-ink-muted">
 {client.displayName || t('common.noName')}
 </span>
 <span className="text-xs text-ink-subtle">
 ID: {client.uid?.substring(0, 8)}...
 </span>
 </div>
 </td>

 <td className="py-3 px-4">
 <div className="flex flex-col gap-1">
 {client.email && (
 <div className="flex items-center gap-1 text-sm text-ink-muted">
 <Mail size={14} />
 {client.email}
 </div>
 )}
 {client.phoneNumber && (
 <div className="flex items-center gap-1 text-sm text-ink-muted">
 <Phone size={14} />
 {client.phoneNumber}
 </div>
 )}
 </div>
 </td>

 <td className="py-3 px-4">
 <div className="flex items-center gap-1 text-sm text-ink-muted">
 <Calendar size={14} />
 {client.createdAt
 ? typeof client.createdAt === 'string'
 ? new Date(client.createdAt).toLocaleDateString('es-AR')
 : (client.createdAt as any).toDate?.().toLocaleDateString('es-AR') || 'N/A'
 : 'N/A'}
 </div>
 </td>

 <td className="py-3 px-4">
 {getStatusBadge(client)}
 {client.isBanned && client.bannedUntil && !client.isDeleted && (
 <p className="text-xs text-danger mt-1">
 {t('admin.clients.until')}: {new Date(client.bannedUntil).toLocaleDateString('es-AR')}
 </p>
 )}
 {client.isDeleted && client.deletedAt && (
 <p className="text-xs text-ink-muted mt-1">
 {t('admin.clients.deletedOn', 'Eliminado')}: {
 typeof client.deletedAt === 'string'
 ? new Date(client.deletedAt).toLocaleDateString('es-AR')
 : client.deletedAt?.toDate?.().toLocaleDateString('es-AR') || 'N/A'
 }
 </p>
 )}
 </td>

 <td className="py-3 px-4">
 <div className="flex justify-end gap-2">
 {/* Show restore button for deleted accounts (super admin only) */}
 {client.isDeleted && isSuperAdmin ? (
 <button
 onClick={() => handleRestoreAccount(client.uid!)}
 disabled={processingAction === client.uid}
 className="p-2 text-success hover:bg-success/20 rounded-lg transition disabled:opacity-50 flex items-center gap-1"
 title={t('admin.clients.restoreAccount', 'Restaurar cuenta')}
 >
 <RefreshCw size={18} />
 </button>
 ) : !client.isDeleted && (
 <>
 <button
 onClick={() => handleEditClient(client)}
 disabled={processingAction === client.uid}
 className="p-2 text-info hover:bg-info/20 rounded-lg transition disabled:opacity-50"
 title={t('admin.clients.editClient')}
 >
 <Edit2 size={18} />
 </button>

 <button
 onClick={() => setStampClient(client)}
 disabled={processingAction === client.uid}
 className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-accent bg-accent/10 hover:bg-accent/20 border border-accent/30 rounded-lg transition disabled:opacity-50"
 title={t('admin.clients.manageStamps')}
 >
 <Star size={14} />
 <span className="hidden sm:inline">{t('admin.clients.stamps.cards', 'Sellos')}</span>
 </button>

 <button
 onClick={() => handleBanClient(client.uid!, client.isBanned || false)}
 disabled={processingAction === client.uid}
 className={`p-2 rounded-lg transition disabled:opacity-50 ${
 client.isBanned
 ? 'text-success hover:bg-success/20'
 : 'text-warning hover:bg-warning/20'
 }`}
 title={client.isBanned ? t('admin.clients.unban') : t('admin.clients.ban')}
 >
 {client.isBanned ? <CheckCircle size={18} /> : <Ban size={18} />}
 </button>

 <button
 onClick={() => handleDeleteClient(client.uid!, client.displayName || client.email || 'Cliente')}
 disabled={processingAction === client.uid}
 className="p-2 text-danger hover:bg-danger/20 rounded-lg transition disabled:opacity-50"
 title={t('admin.clients.deleteClient')}
 >
 <Trash2 size={18} />
 </button>
 </>
 )}
 </div>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>
 </div>

 {/* Stamp Modal */}
 {stampClient && (
 <ClientStampModal
 client={stampClient}
 franchiseId={selectedFranchiseId === 'all' ? 'lILDGxMgfsifzpqg4YZu' : selectedFranchiseId}
 onClose={() => setStampClient(null)}
 />
 )}

 {/* Edit Modal */}
 {editingClient && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="bg-surface rounded-lg max-w-md w-full p-6">
 <h3 className="text-xl font-bold text-ink mb-4">{t('admin.clients.editClient')}</h3>

 <div className="space-y-4">
 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 {t('common.name')}
 </label>
 <input
 type="text"
 value={editingClient.displayName}
 onChange={(e) => setEditingClient({ ...editingClient, displayName: e.target.value })}
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink"
 />
 </div>

 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 {t('common.email')}
 </label>
 <input
 type="email"
 value={editingClient.email}
 disabled
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface-2 text-ink-subtle cursor-not-allowed"
 />
 <p className="text-xs text-ink-subtle mt-1">
 {t('admin.clients.emailCannotBeModified')}
 </p>
 </div>

 <div>
 <label className="block text-sm font-medium text-ink-muted mb-1">
 {t('common.phone')}
 </label>
 <input
 type="tel"
 value={editingClient.phoneNumber}
 onChange={(e) => setEditingClient({ ...editingClient, phoneNumber: e.target.value })}
 className="w-full px-3 py-2 border border-hairline rounded-lg bg-surface text-ink"
 />
 </div>
 </div>

 <div className="flex gap-3 mt-6">
 <button
 onClick={handleSaveEdit}
 disabled={processingAction === editingClient.userId}
 className="flex-1 bg-info text-white px-4 py-2 rounded-lg hover:bg-info/90 disabled:opacity-50 font-medium"
 >
 {t('common.save')}
 </button>
 <button
 onClick={() => setEditingClient(null)}
 className="flex-1 bg-surface-2 text-ink-muted px-4 py-2 rounded-lg hover:bg-surface-3 font-medium"
 >
 {t('common.cancel')}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default ClientsManagement;
